import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from 'bento';
import React, { ReactNode } from 'react';

export interface ConfirmDialogProps {
  /**
   * Button label for the reject action. Default is "Cancel"
   */
  cancelText?: string;
  /**
   * Content of the ConfirmDialog. Strings are displayed as heading04.
   */
  children: ReactNode;
  /**
   * Button label for the accept action. Default is "Confirm"
   */
  continueText?: string;
  /**
   * If true, renders a danger button
   */
  isDestructive?: boolean;
  /**
   * If true, the ConfirmDialog is open
   */
  open: boolean;
  /**
   * Callback fired upon rejection
   */
  onCancel(): void;
  /**
   * Callback fired upon acceptance
   */
  onContinue(value?: unknown): void;
  /**
   * Title of the ConfirmDialog. Default is "Are you sure?"
   */
  title?: string;
}

/**
 * Purpose built dialog component to prompt user for a response.
 * Can be programmatically invoked using AsyncRender.confirm()
 * @deprecated Deprecated — use pantry ConfirmationModal instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-confirmationmodal--docs
 * Migration: Replace ConfirmDialog with the pantry ConfirmationModal component.
 */
export const ConfirmDialog: React.FC<React.PropsWithChildren<ConfirmDialogProps>> = ({
  children,
  cancelText = 'Cancel',
  continueText = 'Confirm',
  isDestructive = false,
  open,
  onCancel,
  onContinue,
  title = 'Are you sure?',
}) => {
  const confirmButtonDisplayProps: Pick<ButtonProps, 'variant' | 'color'> = isDestructive
    ? { color: 'danger', variant: 'contained' }
    : { color: 'primary', variant: 'contained' };

  return (
    <Dialog
      aria-labelledby="confirmTitle"
      fullWidth
      maxWidth="xs"
      open={open}
      data-testid="confirmation-dialog"
    >
      <DialogTitle id="confirmTitle" onClose={onCancel}>
        {title}
      </DialogTitle>
      <DialogContent>
        {typeof children === 'string' ? (
          <Typography variant="body" data-testid="defaultTypography">
            {children}
          </Typography>
        ) : (
          <>{children}</>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="subtle" onClick={onCancel} size="large" variant="text">
          {cancelText}
        </Button>
        <Button
          data-testid="ContinueButton"
          onClick={onContinue}
          size="large"
          {...confirmButtonDisplayProps}
        >
          {continueText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
